import { z } from 'zod';

import { AiActionStatus } from './ai-activity.dto';

/**
 * AI Usage Summary DTO — powers the team AI usage / billing view.
 *
 * Aggregates `core.ai_action_log` for one team over a rolling window so
 * ops + customers can see how many AI calls ran, what they cost, and how
 * spend breaks down by feature, outcome, and day.  Feeds `GET /api/ai/usage`.
 */

export const AiUsageTotalsSchema = z.object({
  calls: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
  avgLatencyMs: z.number().nonnegative(),
});
export type AiUsageTotals = z.infer<typeof AiUsageTotalsSchema>;

export const AiUsageByFeatureSchema = z.object({
  feature: z.string(),
  calls: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
});
export type AiUsageByFeature = z.infer<typeof AiUsageByFeatureSchema>;

export const AiUsageByStatusSchema = z.object({
  status: AiActionStatus,
  calls: z.number().int().nonnegative(),
});
export type AiUsageByStatus = z.infer<typeof AiUsageByStatusSchema>;

export const AiUsageByDaySchema = z.object({
  /** UTC calendar day, YYYY-MM-DD. */
  day: z.string(),
  calls: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
});
export type AiUsageByDay = z.infer<typeof AiUsageByDaySchema>;

export const AiUsageSummarySchema = z.object({
  teamId: z.string().uuid(),
  /** Inclusive lower bound of the window (UTC ISO). */
  since: z.string(),
  /** Number of days the window spans (1..90). */
  windowDays: z.number().int().positive(),
  totals: AiUsageTotalsSchema,
  byFeature: z.array(AiUsageByFeatureSchema),
  byStatus: z.array(AiUsageByStatusSchema),
  byDay: z.array(AiUsageByDaySchema),
});
export type AiUsageSummary = z.infer<typeof AiUsageSummarySchema>;
